import { useEffect } from 'react';
import Modal from '@organisms/Modal/Modal';
import { trackBankIdModalShown } from '@helpers/analyticsHelpers/trackBankId';
import { useBankIdSignContext } from '@organisms/BankIdModal/BankIdSignContextProvider';
import {
  COMPLETE,
  CUSTOMERNOTFOUND,
  FAILED,
  LOGIN_OUTSTANDING_TRANSACTION,
  LOGIN_USER_CANCEL,
  NO_DEVICE,
  OTHER_DEVICE,
  SAME_DEVICE,
  SELECT_SIGN_IN_METHOD,
  UNDEFINED,
} from '@constants/bankid';
import BankIdQr from '@organisms/BankIdLoginFlow/BankIdLoginModal/BankIdQr/BankIdQr';
import {
  CompleteAuth,
  FailedAuth,
  PendingAuth,
} from '@organisms/BankIdLoginFlow/BankIdLoginModal/AuthStatuses/AuthStatuses';
import { StyledButton, StyledButtonWrapper, StyledContentWrapper } from './BankIdModal.styles';
import useBankIdSign from '@organisms/BankIdModal/useBankIdSign';
import useTranslation from 'next-translate/useTranslation';
import Heading from '@atoms/Heading/Heading';
import Image from 'next/image';
import UserAgent from '@helpers/useragent';

interface Props {
  onClose: () => void;
  onSuccess: (orderRef: string) => void;
}

const BankIdModal = ({ onClose, onSuccess }: Props) => {
  const { startBankIdSign, handleCancel, handleRetry, hasError } = useBankIdSign({
    onSuccess,
  });
  const { hintCode, currentStatus, bankIdDevice, setBankIdDevice, qrData } = useBankIdSignContext();

  const { t } = useTranslation('bankid');
  const isQr = bankIdDevice === OTHER_DEVICE;
  const showQrPending = isQr && hintCode !== LOGIN_OUTSTANDING_TRANSACTION;
  const showSameDevicePending = bankIdDevice === SAME_DEVICE && currentStatus !== SELECT_SIGN_IN_METHOD;
  const showError =
    bankIdDevice !== NO_DEVICE &&
    (hasError || currentStatus === FAILED || currentStatus === UNDEFINED || currentStatus === CUSTOMERNOTFOUND);
  const isCompleted = currentStatus === COMPLETE;
  const showPending = (showQrPending || showSameDevicePending) && !showError && !isCompleted;

  const handleCancelClick = () => {
    setBankIdDevice(NO_DEVICE);
    // If the user has cancelled the login request in app, let the handler know about that, so we won't send the cancel
    // request to BankId, because the order ref will be automatically cancelled by them.
    const userCancelledLoginInBankIdApp = hintCode === LOGIN_USER_CANCEL;
    handleCancel(userCancelledLoginInBankIdApp);
  };

  const handleCloseModal = () => {
    handleCancelClick();
    onClose();
  };

  const handleRetryClick = () => {
    handleRetry();
    startBankIdSign({
      mobile: UserAgent.isMobileDevice(),
      sameDevice: bankIdDevice === SAME_DEVICE,
      isIos: UserAgent.isIOS(),
    });
  };

  useEffect(() => {
    // Start auth flow when modal is opened and selected device is same device
    // Wait for client to be ready
    if (bankIdDevice === SAME_DEVICE) {
      startBankIdSign({ mobile: UserAgent.isMobileDevice(), sameDevice: true, isIos: UserAgent.isIOS() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankIdDevice]);

  useEffect(() => {
    trackBankIdModalShown(true);
  }, []);

  return (
    <Modal size="medium" onClose={handleCloseModal} verticallyCentered hasCloseIcon>
      <StyledContentWrapper>
        {bankIdDevice === NO_DEVICE && (
          <>
            <Heading variant="h2" size="small">
              {t('bankid:status->SELECT_SIGN_IN_METHOD')}
            </Heading>
            <StyledButtonWrapper>
              <StyledButton onClick={() => setBankIdDevice(SAME_DEVICE)}>
                {t('login:login->bankId->startBankIdSameDeviceText')}
              </StyledButton>

              <StyledButton onClick={() => setBankIdDevice(OTHER_DEVICE)}>
                {t('login:login->bankId->bankIdOnOtherDeviceText')}
              </StyledButton>
            </StyledButtonWrapper>
          </>
        )}

        {bankIdDevice !== NO_DEVICE && (
          <Image
            unoptimized
            src="/icons/logos/BankID.svg"
            alt="BankID"
            width={90}
            height={86}
            style={{ height: 'auto' }}
          />
        )}

        {bankIdDevice === OTHER_DEVICE && !isCompleted && (
          <>
            <BankIdQr
              startBankIdFlow={startBankIdSign}
              handleCancel={handleCancelClick}
              showPending={showPending}
              showError={showError}
              qrData={qrData}
            />
          </>
        )}

        {bankIdDevice && showPending && (
          <PendingAuth
            handleCancel={handleCancelClick}
            hintCode={hintCode}
            isSameDevice={bankIdDevice === SAME_DEVICE}
          />
        )}

        {bankIdDevice && isCompleted && <CompleteAuth />}

        {showError && <FailedAuth handleReturnClick={handleCancelClick} hintCode={hintCode} isQr={isQr} />}
      </StyledContentWrapper>
    </Modal>
  );
};

export default BankIdModal;
